import React, { useEffect, useMemo, useState } from 'react';
import { collection, onSnapshot, orderBy, query, where, Timestamp } from 'firebase/firestore';
import { db } from '../../../../firebase';

type EntityRef = { type: string; id: string; label?: string };
type ActivityEvent = {
  id: string;
  customerId: string;
  eventType: string;
  primaryEntity: EntityRef;
  title?: string;
  linkUrl?: string;
  timestamp?: Timestamp | any;
};

const fmt = (ts?: ActivityEvent['timestamp']) => {
  if (!ts) return '';
  try {
    if (ts instanceof Timestamp) return ts.toDate().toLocaleString();
    if (typeof ts === 'object' && ts.seconds) return new Date(ts.seconds * 1000).toLocaleString();
    if (ts instanceof Date) return ts.toLocaleString();
  } catch {}
  return '';
};

export default function SelectedClientActivity({ customerId }: { customerId: string | null }) {
  const [events, setEvents] = useState<ActivityEvent[]>([]);

  useEffect(() => {
    if (!customerId) { setEvents([]); return; }
    const q = query(
      collection(db, 'customer_activity'),
      where('customerId', '==', customerId),
      orderBy('timestamp', 'desc')
    );
    const unsub = onSnapshot(q, (snap) => {
      setEvents(snap.docs.map(d => ({ id: d.id, ...(d.data() as any) })));
    });
    return () => unsub();
  }, [customerId]);

  const recent = useMemo(() => events.slice(0, 20), [events]);

  if (!customerId) return <div className='text-muted'>Select a customer to view activity</div>;

  return (
    <div>
      <div className='d-flex justify-content-between align-items-center mb-3'>
        <h5 className='mb-0'>Recent Activity</h5>
      </div>
      <div className='table-responsive'>
        <table className='table align-middle table-row-dashed fs-7 gy-3'>
          <thead>
            <tr className='text-start text-muted fw-bold text-uppercase gs-0'>
              <th style={{width: 160}}>Time</th>
              <th>Type</th>
              <th>Title</th>
              <th>Entity</th>
              <th style={{width: 80}}>Link</th>
            </tr>
          </thead>
          <tbody className='text-gray-700 fw-semibold'>
            {recent.map((e) => (
              <tr key={e.id}>
                <td className='text-nowrap'>{fmt(e.timestamp)}</td>
                <td><span className='badge badge-light-primary'>{e.eventType}</span></td>
                <td className='text-truncate' style={{maxWidth: 300}} title={e.title || ''}>{e.title || ''}</td>
                <td>{e.primaryEntity?.type} #{e.primaryEntity?.id?.slice(0,6)}</td>
                <td>{e.linkUrl ? <a href={e.linkUrl} className='btn btn-xs btn-light-primary'>Open</a> : <span className='text-muted'>—</span>}</td>
              </tr>
            ))}
            {recent.length === 0 && (
              <tr><td colSpan={5} className='text-center text-muted py-5'>No recent activity</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}


